import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import {
  getTikTokSamplePolicy,
  updateTikTokSamplePolicy,
  type TikTokSamplePolicy,
  type TikTokSamplePolicyMode,
} from "@/services/tiktokShop.service";

import { errorMessage } from "../tiktokFormat";

const MODES: Array<{ value: TikTokSamplePolicyMode; title: string; body: string }> = [
  { value: "OFF", title: "Manual", body: "Every request waits for you, with no suggestion." },
  { value: "SUGGEST", title: "Suggest", body: "Each request shows approve, reject or needs a look. You decide." },
  { value: "AUTO", title: "Auto-review", body: "Reveshare approves (and optionally rejects) for you every 15 minutes. Unclear cases stay for you." },
];

const NUMBER_FIELDS: Array<{ key: keyof TikTokSamplePolicy; label: string; hint: string; prefix?: string; suffix?: string; max?: number }> = [
  { key: "minFollowers", label: "Minimum followers", hint: "e.g. 10000" },
  { key: "minGmv", label: "Minimum 30-day GMV", hint: "e.g. 1000", prefix: "$" },
  { key: "minFulfillmentRate", label: "Minimum sample fulfillment", hint: "e.g. 60", suffix: "%", max: 100 },
  { key: "minMedianViews", label: "Minimum median video views", hint: "e.g. 5000" },
];

const toNumber = (value: string): number | null => {
  const trimmed = value.replace(/[,\s]/g, "");
  if (!trimmed) return null;
  const n = Math.round(Number(trimmed));
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/** How Reveshare treats new sample requests: manual, suggest or decide. */
export function SamplePolicySheet({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const queryClient = useQueryClient();
  const policy = useQuery({ queryKey: ["tiktok-sample-policy"], queryFn: getTikTokSamplePolicy, enabled: open });
  const [draft, setDraft] = useState<TikTokSamplePolicy | null>(null);

  useEffect(() => {
    if (open && policy.data) setDraft(policy.data);
  }, [open, policy.data]);

  const save = useMutation({
    mutationFn: updateTikTokSamplePolicy,
    onSuccess: (saved) => {
      queryClient.setQueryData(["tiktok-sample-policy"], saved);
      queryClient.invalidateQueries({ queryKey: ["tiktok-samples"] });
      toast.success(saved.mode === "AUTO" ? "Auto-review is on" : "Review rules saved");
      onOpenChange(false);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const set = <K extends keyof TikTokSamplePolicy>(key: K, value: TikTokSamplePolicy[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  const hasThreshold = draft ? NUMBER_FIELDS.some((f) => draft[f.key] !== null) : false;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col overflow-y-auto sm:max-w-md">
        <SheetHeader className="text-left">
          <SheetTitle>Review rules</SheetTitle>
          <SheetDescription>
            Who gets a free sample once they ask. TikTok’s own rules (Who can request) already filter who may ask at all.
          </SheetDescription>
        </SheetHeader>

        {draft && (
          <div className="mt-6 flex-1 space-y-6">
            <fieldset className="space-y-2">
              <legend className="mb-2 text-sm font-medium text-foreground">Mode</legend>
              {MODES.map((m) => (
                <label
                  key={m.value}
                  className={cn(
                    "flex cursor-pointer gap-3 rounded-lg border p-3",
                    draft.mode === m.value ? "border-brand-500 bg-brand-50 dark:bg-brand-950/40" : "border-border",
                  )}
                >
                  <input
                    type="radio"
                    name="sample-mode"
                    className="mt-1 accent-[hsl(var(--primary))]"
                    checked={draft.mode === m.value}
                    onChange={() => set("mode", m.value)}
                  />
                  <span>
                    <span className="block text-sm font-medium text-foreground">{m.title}</span>
                    <span className="block text-xs text-muted-foreground">{m.body}</span>
                  </span>
                </label>
              ))}
            </fieldset>

            <fieldset className="space-y-3" disabled={draft.mode === "OFF"}>
              <legend className="mb-1 text-sm font-medium text-foreground">Approve creators who meet every rule you set</legend>
              {NUMBER_FIELDS.map((f) => (
                <div key={f.key} className="grid grid-cols-[1fr_9rem] items-center gap-3">
                  <Label htmlFor={`policy-${f.key}`} className="text-sm font-normal text-foreground">
                    {f.label}
                  </Label>
                  <div className="relative">
                    {f.prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{f.prefix}</span>}
                    <Input
                      id={`policy-${f.key}`}
                      inputMode="numeric"
                      placeholder={f.hint}
                      className={cn(f.prefix && "pl-6", f.suffix && "pr-7")}
                      value={draft[f.key] === null ? "" : String(draft[f.key])}
                      onChange={(e) => {
                        const n = toNumber(e.target.value);
                        set(f.key, (f.max !== undefined && n !== null ? Math.min(n, f.max) : n) as never);
                      }}
                    />
                    {f.suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{f.suffix}</span>}
                  </div>
                </div>
              ))}
              <p className="text-xs text-muted-foreground">
                A figure the creator keeps private never counts against them; that request waits for you instead.
              </p>

              <div className="flex items-start justify-between gap-4 pt-2">
                <span>
                  <span className="block text-sm text-foreground">Approve my affiliates</span>
                  <span className="block text-xs text-muted-foreground">Creators already approved in a Reveshare program skip the rules.</span>
                </span>
                <Switch checked={draft.approveAffiliates} onCheckedChange={(v) => set("approveAffiliates", v)} />
              </div>
              <div className="flex items-start justify-between gap-4">
                <span>
                  <span className="block text-sm text-foreground">Reject below the rules</span>
                  <span className="block text-xs text-muted-foreground">Off: requests that miss a rule wait for you. On: they are rejected as not a fit.</span>
                </span>
                <Switch checked={draft.rejectBelow} onCheckedChange={(v) => set("rejectBelow", v)} />
              </div>
              <div className="grid grid-cols-[1fr_9rem] items-center gap-3">
                <Label htmlFor="policy-cap" className="text-sm font-normal text-foreground">
                  Daily approval cap
                  <span className="block text-xs text-muted-foreground">Automatic approvals per day; blank for no cap</span>
                </Label>
                <Input
                  id="policy-cap"
                  inputMode="numeric"
                  placeholder="No cap"
                  value={draft.dailyApprovalCap === null ? "" : String(draft.dailyApprovalCap)}
                  onChange={(e) => {
                    const n = toNumber(e.target.value);
                    set("dailyApprovalCap", n === 0 ? null : n);
                  }}
                />
              </div>
            </fieldset>

            {draft.mode !== "OFF" && !hasThreshold && !draft.approveAffiliates && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                With no rules set, every request is marked “needs a look”.
              </p>
            )}
          </div>
        )}

        <SheetFooter className="mt-6 gap-2 sm:justify-end">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={!draft || save.isPending} onClick={() => draft && save.mutate(draft)}>
            {save.isPending ? "Saving…" : "Save rules"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
